import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
  Visibility,
  VisibilityOff,
  ContentCopy,
  Edit,
  Save,
  Cancel,
  CheckCircle
} from '@mui/icons-material';
import { useApiKey } from '../../hooks/useApiKey';
import { DateTime } from 'luxon';

const SnackbarAlert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ApiKeyManagement = () => {
  const {
    apiKey,
    isSet,
    lastUpdated,
    isLoading,
    error,
    updateApiKey,
    copyApiKey,
  } = useApiKey();

  const [showApiKey, setShowApiKey] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleToggleVisibility = () => {
    setShowApiKey(!showApiKey);
  };

  const handleCopy = async () => {
    const success = await copyApiKey();
    setNotification({
      open: true,
      message: success ? 'API key copied to clipboard!' : 'Failed to copy API key',
      severity: success ? 'success' : 'error'
    });
  };

  const handleStartEdit = () => {
    setEditValue(apiKey);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditValue('');
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    if (editValue.trim() !== apiKey) {
      setShowUpdateDialog(true);
    } else {
      handleCancelEdit();
    }
  };

  const handleConfirmUpdate = async () => {
    setShowUpdateDialog(false);
    const success = await updateApiKey(editValue.trim());
    
    if (success) {
      setNotification({
        open: true,
        message: 'API key updated successfully!',
        severity: 'success'
      });
      setIsEditing(false);
      setEditValue('');
    } else {
      setNotification({
        open: true,
        message: error || 'Failed to update API key',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const formatLastUpdated = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
    } catch {
      return 'Unknown';
    }
  };

  const displayValue = () => {
    if (isEditing) return editValue;
    if (!isSet) return '';
    if (showApiKey) return apiKey;
    return '•'.repeat(Math.min(apiKey.length, 40));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Pabau API Key Management
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Your Pabau API key is used to sync data from your Pabau account. 
          Keep this key secure and only share it with trusted team members.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          API Key Status
        </Typography>
        <Chip
          icon={isSet ? <CheckCircle /> : undefined}
          label={isSet ? 'Configured' : 'Not Set'}
          color={isSet ? 'success' : 'warning'}
          variant={isSet ? 'filled' : 'outlined'}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          API Key
        </Typography>
        <TextField
          fullWidth
          value={displayValue()}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder={isSet ? "API key is set" : "Enter your Pabau API key"}
          disabled={!isEditing || isLoading}
          type={showApiKey || isEditing ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {!isEditing && (
                  <>
                    {isSet && (
                      <IconButton
                        onClick={handleToggleVisibility}
                        disabled={isLoading}
                        size="small"
                      >
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    )}
                    {isSet && showApiKey && (
                      <IconButton
                        onClick={handleCopy}
                        disabled={isLoading}
                        size="small"
                      >
                        <ContentCopy />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={handleStartEdit}
                      disabled={isLoading}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                  </>
                )}
                {isEditing && (
                  <>
                    <IconButton
                      onClick={handleSaveEdit}
                      disabled={isLoading}
                      size="small"
                      color="primary"
                    >
                      <Save />
                    </IconButton>
                    <IconButton
                      onClick={handleCancelEdit}
                      disabled={isLoading}
                      size="small"
                    >
                      <Cancel />
                    </IconButton>
                  </>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isSet && (
        <Typography variant="caption" color="text.secondary">
          Last updated: {formatLastUpdated(lastUpdated)}
        </Typography>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {isEditing ? 'Updating API key...' : 'Loading API key...'}
          </Typography>
        </Box>
      )}

      {/* Update Confirmation Dialog */}
      <Dialog open={showUpdateDialog} onClose={() => setShowUpdateDialog(false)}>
        <DialogTitle>Update API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update your Pabau API key? This will affect data synchronization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpdateDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmUpdate} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <SnackbarAlert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </SnackbarAlert>
      </Snackbar>
    </Paper>
  );
};

export default ApiKeyManagement; 